import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, ChevronDown, MapPin } from "lucide-react";
import LogoText from "./LogoText";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useActiveTransferDestinations } from "@/hooks/useTransferDestinations";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { getConfig } = useSiteConfig();
  const { data: destinations = [] } = useActiveTransferDestinations();
  
  // Detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Get top 6 most popular destinations (sorted by display_order)
  const popularDestinations = destinations
    .sort((a, b) => a.display_order - b.display_order)
    .slice(0, 6);

  const navigationItems = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Transfers", href: "/transfers" },
    { name: "Transfer Destinations", href: "/transfer-destinations" },
    { name: "Tours", href: "/tours" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <header className={`bg-background backdrop-blur-md border-b border-border sticky top-0 z-50 transition-shadow duration-300 ${
      isScrolled ? 'shadow-2xl shadow-primary/20' : 'shadow-lg'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 hover:opacity-90 transition-opacity">
            <LogoText className="h-16" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-12">
            <NavigationMenu>
              <NavigationMenuList>
                {navigationItems.map((item) => {
                  if (item.name === "Transfer Destinations") {
                    return (
                      <NavigationMenuItem key={item.name}>
                        <NavigationMenuTrigger 
                          className={`font-medium transition-all hover:text-primary relative text-sm uppercase tracking-wide bg-transparent ${
                            isActive(item.href) ? "text-primary" : "text-foreground"
                          }`}
                        >
                          {item.name}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent className="z-50">
                          <div className="w-[600px] bg-background/95 backdrop-blur-md border-2 border-primary/20 rounded-lg shadow-2xl p-6">
                            <div className="mb-4">
                              <h3 className="text-lg font-bold text-foreground mb-2">Popular Destinations</h3>
                              <p className="text-sm text-muted-foreground">Explore our most requested transfer destinations</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              {popularDestinations.map((destination) => (
                                <Link
                                  key={destination.id}
                                  to={`/transfer-destinations/${destination.slug}`}
                                  className="group flex items-start gap-3 p-3 rounded-lg hover:bg-primary/10 transition-all border border-transparent hover:border-primary/20"
                                >
                                  <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                                      {destination.name}
                                    </div>
                                    {destination.from_airport_price && (
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        From ${destination.from_airport_price}
                                      </div>
                                    )}
                                  </div>
                                </Link>
                              ))}
                            </div>
                            <Link
                              to="/transfer-destinations"
                              className="mt-4 block text-center text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                            >
                              View All Destinations →
                            </Link>
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  }
                  
                  return (
                    <NavigationMenuItem key={item.name}>
                      <Link
                        to={item.href}
                        className={`font-medium transition-all hover:text-primary relative text-sm uppercase tracking-wide ${
                          isActive(item.href) ? "text-primary" : "text-foreground"
                        }`}
                      >
                        {item.name}
                        {isActive(item.href) && (
                          <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary rounded-full" />
                        )}
                      </Link>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <Phone className="h-4 w-4" />
              <span className="font-medium">{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
            </div>
            <Link to="/transfers">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all duration-300 hover:scale-105 font-semibold">
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="outline" size="icon" className="border-border text-foreground hover:bg-muted">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background text-foreground border-border">
              <div className="flex flex-col space-y-6 mt-6">
                <div className="flex items-center space-x-2">
                  <LogoText className="h-12" />
                </div>
                
                <nav className="flex flex-col space-y-4">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`font-medium transition-colors hover:text-primary text-lg ${
                        isActive(item.href) ? "text-primary" : "text-foreground"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>

                <div className="flex flex-col space-y-4 pt-6 border-t border-border">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
                  </div>
                  <Link to="/transfers" onClick={() => setIsOpen(false)}>
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg w-full font-semibold">
                      Book Now
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
